import React, { useEffect, useRef, useState } from "react";
import { AppBar, Box, IconButton, InputAdornment, TextField, useMediaQuery, Autocomplete, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert, Chip, Snackbar, Popover } from "@mui/material";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { Button, Toolbar, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import DateRangePicker from "./common/DateRangePicker";
import DateRangeIcon from "@mui/icons-material/DateRange";
import dayjs from "dayjs";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningIcon from "@mui/icons-material/Warning";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const STATUS_OPTIONS = [
  { label: "PASS", value: "good workpiece" },
  { label: "REJECT", value: "defective" },
];

const OPERATOR_STATUS_OPTIONS = [
  { label: "All Overrides", value: "all", chipLabel: "Operator: Overridden" },
  { label: "Operator: PASS", value: "PASS", chipLabel: "Operator: PASS" },
  { label: "Operator: REJECT", value: "REJECT", chipLabel: "Operator: REJECT" },
];

const AppBarCommon = (props) => {
  const {
    name,
    searchTerm,
    setSearchTerm,
    selectedDate,
    setSelectedDate,
    deviceFilter,
    setDeviceFilter,
    deviceOptions = [],
    statusFilter,
    setStatusFilter,
    operatorStatusFilter,
    setOperatorStatusFilter,
    filterMetadata,
    loading,
  } = props;
  const navigate = useNavigate();
  const { mobileOpen, drawerWidth } = useOutletContext();

  const [dateRangeAnchor, setDateRangeAnchor] = useState(null);

  const formatDateRangeLabel = (sd) => {
    if (!sd) return null;
    const start = sd.startDate ? dayjs(sd.startDate) : (sd.format ? dayjs(sd) : null);
    const end = sd.endDate ? dayjs(sd.endDate) : (sd.format ? dayjs(sd) : null);

    if (start && end && start.isValid() && end.isValid()) {
      if (start.isSame(end, "day")) {
        return start.format("MMM DD, YYYY");
      }
      if (start.year() === end.year()) {
        return `${start.format("MMM DD")} - ${end.format("MMM DD, YYYY")}`;
      }
      return `${start.format("MMM DD, YYYY")} - ${end.format("MMM DD, YYYY")}`;
    }
    if (start && start.isValid()) return `From ${start.format("MMM DD, YYYY")}`;
    if (end && end.isValid()) return `Until ${end.format("MMM DD, YYYY")}`;
    return null;
  };

  const handleOpenDateRange = (event) => {
    setDateRangeAnchor(event.currentTarget);
  };

  const handleClearDateRange = (e) => {
    if (e) e.stopPropagation();
    setSelectedDate(null);
    setDateRangeAnchor(null);
  };

  const appendDateFilterToUrl = (baseUrl, sd) => {
    if (!sd) return baseUrl;
    const start = sd.startDate ? dayjs(sd.startDate) : (sd.format ? dayjs(sd) : null);
    const end = sd.endDate ? dayjs(sd.endDate) : (sd.format ? dayjs(sd) : null);

    if (start && end && start.isValid() && end.isValid()) {
      if (start.isSame(end, "day")) {
        return `${baseUrl}&filter_created_date:eq=${start.format("YYYY-MM-DD")}`;
      }
      return `${baseUrl}&filter_created_date:gte=${start.format("YYYY-MM-DD")}&filter_created_date:lte=${end.format("YYYY-MM-DD")}`;
    }
    if (start && start.isValid()) {
      return `${baseUrl}&filter_created_date:gte=${start.format("YYYY-MM-DD")}`;
    }
    if (end && end.isValid()) {
      return `${baseUrl}&filter_created_date:lte=${end.format("YYYY-MM-DD")}`;
    }
    return baseUrl;
  };
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef(null);
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFormat, setPendingFormat] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const axiosPrivate = useAxiosPrivate();

  useEffect(() => {
      setLocalSearch(searchTerm);
    }, [searchTerm]);
  
  const isCompact = useMediaQuery("(max-width:768px)");

  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);
  

  useEffect(() => {
    const fetchSuggestions = async () => {
      setLoadingSuggestions(true);
      try {
        const q = localSearch || "";
        const response = await axiosPrivate.get(`/defect-inspection/v1/suggestions?q=${encodeURIComponent(q)}`);
        setSuggestions(response?.data?.suggestions || []);
      } catch (err) {
        console.error("Failed to fetch suggestions:", err);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, localSearch ? 500 : 0);
    return () => clearTimeout(timeoutId);
  }, [localSearch, axiosPrivate]);

  const handleSearchChange = (event, newValue) => {
    setLocalSearch(newValue);
    setSearchTerm(newValue); 
  };

  const calendarIconRef = useRef(null);

  const handleOpenFilter = (e) => {
    setFilterAnchor(e.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchor(null);
  };

  const handleDeviceSelect = (device) => {
    setDeviceFilter(device);
    setFilterAnchor(null);
  };

  const handleStatusSelect = (statusValue) => {
    if (setStatusFilter) {
      if (statusFilter === statusValue) {
        setStatusFilter(null);
      } else {
        setStatusFilter(statusValue);
      }
    }
    setFilterAnchor(null);
  };

  const handleOperatorStatusSelect = (val) => {
    if (setOperatorStatusFilter) {
      if (operatorStatusFilter === val) {
        setOperatorStatusFilter(null);
      } else {
        setOperatorStatusFilter(val);
      }
    }
    setFilterAnchor(null);
  };

  const handleOpenExport = (e) => {
    setExportAnchor(e.currentTarget);
  };

  const handleCloseExport = () => {
    setExportAnchor(null);
  };

  const triggerExport = (format) => {
    setPendingFormat(format);
    setConfirmDialogOpen(true);
    handleCloseExport();
  };

  const handleExecuteExport = (action) => {
    if (action === 'download') {
      const limit = pendingFormat === 'pdf' ? 100 : 1000;
      const isLimited = (props.totalCount || 0) > limit;
      
      setSnackbarMessage(isLimited 
        ? `Downloading the first ${limit} records. For full data, use "Export Full".` 
        : "Preparing download...");
      setOpenSnackbar(true);
      
      handleExport();
    } else {
      setSnackbarMessage("Full export started! It will be available in Settings > Exports once ready.");
      setOpenSnackbar(true);
      handleEmailExport();
    }
  };

  const handleExport = async () => {
    const format = pendingFormat;
    setConfirmDialogOpen(false);
    setExportLoading(true);
    try {
      let url = `/defect-inspection/v1/export_events?format=${format}`;
      if (localSearch) url += `&search=${encodeURIComponent(localSearch)}`;
      url = appendDateFilterToUrl(url, selectedDate);
      if (statusFilter) {
        url += `&filter_status:eq=${encodeURIComponent(statusFilter)}`;
      }
      if (operatorStatusFilter) {
        url += `&filter_operator_status:eq=${encodeURIComponent(operatorStatusFilter)}`;
      }
      if (deviceFilter) {
        url += `&filter_device_name:eq=${encodeURIComponent(deviceFilter)}`;
      }

      const response = await axiosPrivate.get(url, { responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      const filename = format === 'pdf' ? `events_report_${timestamp}.pdf` : `events_export_${timestamp}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleEmailExport = async () => {
    const format = pendingFormat;
    setConfirmDialogOpen(false);
    setExportLoading(true);
    try {
      let url = `/defect-inspection/v1/email_export_events?format=${format}`;
      if (localSearch) url += `&search=${encodeURIComponent(localSearch)}`;
      url = appendDateFilterToUrl(url, selectedDate);
      if (statusFilter) {
        url += `&filter_status:eq=${encodeURIComponent(statusFilter)}`;
      }
      if (operatorStatusFilter) {
        url += `&filter_operator_status:eq=${encodeURIComponent(operatorStatusFilter)}`;
      }
      if (deviceFilter) {
        url += `&filter_device_name:eq=${encodeURIComponent(deviceFilter)}`;
      }

      const response = await axiosPrivate.get(url);
      setSnackbarMessage(response.data.message || "Export started. You will receive an email shortly.");
      setOpenSnackbar(true);
    } catch (err) {
      console.error("Email export failed:", err);
      const errMsg = err.response?.data?.message || "Failed to start email export. Please contact your administrator.";
      setSnackbarMessage(errMsg);
      setOpenSnackbar(true);
    } finally {
      setExportLoading(false);
    }
  };

  const activeFilters = [];
  if (statusFilter) {
    const label = STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label;
    if (label) activeFilters.push(label);
  }
  if (operatorStatusFilter) {
    const opt = OPERATOR_STATUS_OPTIONS.find((o) => o.value === operatorStatusFilter);
    if (opt) activeFilters.push(opt.chipLabel);
  }
  if (deviceFilter) {
    activeFilters.push(deviceFilter);
  }
  const activeFilterLabel = activeFilters.length > 0 ? activeFilters.join(" + ") : null;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          boxShadow: "none",
          justifyContent: "center",
          top: 0,
          left: `calc(${drawerWidth}px + 1rem)`,
          right: "1rem",
          zIndex: 1200,
          width: `calc(100% - ${drawerWidth}px - 2rem)`,
          borderRadius: "8px",
          padding: "1rem 0",
        }}
        style={{ backgroundColor: "#eaedeb"}}
      >
        <Toolbar
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 1, sm: 0.5 },
            overflowX: "auto",
          }}
        >
          
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              gap: 2.5,
              flex: "0 1 auto",
              minWidth: 0,
            }}
          >
            {props.backButton && (
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  color: "black",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography
              sx={{
                flex: "0 0 auto",
                fontFamily: "Work Sans",
                color: "rgb(94 89 89)",
                fontWeight: 600,
                fontSize: { xs: "1.5rem", md: "2rem" },
                position: "relative",
                minWidth: 0,
              }}
              noWrap
              component="div"
            >
              {name}
              <Box
                component="span"
                sx={{
                  display: "block",
                  width: "40%",
                  height: 5,
                  backgroundColor: "#49758d",
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                }}
              />
            </Typography>
            {(props.viewEvents || props.showSearch) && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: 1,
                  alignItems: "center",
                  flex: "1 1 auto",
                  minWidth: 0,
                  overflowX: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: isCompact ? (showSearchInput ? 220 : 50) : "100%",
                    maxWidth: isCompact ? (showSearchInput ? 220 : 50) : 450,
                    minWidth: isCompact ? 0 : 350,
                    transition: "width 200ms ease",
                    borderRadius: "20px",
                    backgroundColor: "#f6fbfc",
                    boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
                    padding: isCompact ? "4px" : "4px 16px",
                    overflow: "hidden",
                    minHeight: "40px",
                  }}
                >
                  <IconButton
                    onClick={() => {
                      if (isCompact) {
                        setShowSearchInput((prev) => !prev);
                      }
                    }}
                    sx={{
                      p: 0.5,
                      color: "#007c92",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <SearchIcon />
                  </IconButton>

                  {(!isCompact || showSearchInput) && (
                    <Autocomplete
                      freeSolo
                      disableClearable
                      openOnFocus
                      options={suggestions}
                      getOptionLabel={(option) => (typeof option === 'string' ? option : option.value)}
                      onInputChange={(event, newInputValue) => {
                        setLocalSearch(newInputValue);
                        setSearchTerm(newInputValue);
                      }}
                      onChange={(event, newValue) => {
                        const val = typeof newValue === 'string' ? newValue : newValue?.value;
                        setLocalSearch(val || "");
                        setSearchTerm(val || "");
                      }}
                      value={localSearch}
                      loading={loadingSuggestions}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={searchInputRef}
                          variant="standard"
                          placeholder={props.searchPlaceholder || "Search by Event ID, Profile, PID or Order number"}
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: (
                              <>
                                {loadingSuggestions ? <CircularProgress color="inherit" size={16} /> : null}
                                {localSearch ? (
                                  <IconButton
                                    onClick={() => {
                                      setLocalSearch("");
                                      setSearchTerm("");
                                    }}
                                    sx={{ p: 0.5 }}
                                  >
                                    <ClearIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                            sx: {
                              fontSize: isCompact ? 12 : 14,
                            },
                          }}
                          inputProps={{
                            ...params.inputProps,
                            sx: {
                              fontFamily: "Work Sans",
                              fontSize: isCompact ? 12 : 14,
                              "&::placeholder": {
                                color: "#14285A4D",
                                opacity: 1,
                              },
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <li key={key} {...otherProps} style={{ fontFamily: "Work Sans", fontSize: "13px" }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <span>{option.value}</span>
                              <Box component="span" sx={{ 
                                fontSize: "10px", 
                                color: "#316C7A", 
                                backgroundColor: "#f0f7f8", 
                                padding: "1px 6px", 
                                borderRadius: "8px",
                                fontWeight: 600
                              }}>
                                {option.type}
                              </Box>
                            </Box>
                          </li>
                        );
                      }}
                    />
                  )}
                </Box>

                {props.viewEvents && (
                  <>
                    <Box
                      ref={calendarIconRef}
                      onClick={handleOpenDateRange}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        padding: isCompact ? "4px 8px" : "4px 14px",
                        borderRadius: "24px",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 0 6px rgba(0,0,0,0.08)",
                        cursor: "pointer",
                        fontFamily: "Work Sans",
                        fontSize: isCompact ? "11px" : "14px",
                        color: "#14285A",
                        minHeight: "40px",
                        flex: "0 0 auto",
                        minWidth: isCompact ? "auto" : "160px",
                        border: formatDateRangeLabel(selectedDate) ? "1px solid #007c92" : "none",
                      }}
                    >
                      {!formatDateRangeLabel(selectedDate) && <DateRangeIcon sx={{ color: "#007c92" }} />}

                      {formatDateRangeLabel(selectedDate) ? (
                        <>
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 500,
                              fontSize: isCompact ? "11px" : "13px",
                              whiteSpace: "nowrap",
                              color: "#007c92",
                            }}
                          >
                            {formatDateRangeLabel(selectedDate)}
                          </Box>

                          <IconButton
                            onClick={handleClearDateRange}
                            sx={{
                              width: isCompact ? 20 : 22,
                              height: isCompact ? 20 : 22,
                              backgroundColor: "#eee",
                              ml: 0.5,
                              "&:hover": { backgroundColor: "#ddd" },
                            }}
                          >
                            <ClearIcon sx={{ fontSize: isCompact ? 14 : 16 }} />
                          </IconButton>
                        </>
                      ) : (
                        !isCompact && (
                          <Box component="span" sx={{ color: "#9aa7b2" }}>
                            Select date range
                          </Box>
                        )
                      )}
                    </Box>

                    <Popover
                      open={Boolean(dateRangeAnchor)}
                      anchorEl={dateRangeAnchor}
                      onClose={() => setDateRangeAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      PaperProps={{
                        sx: {
                          p: 2,
                          mt: 1,
                          borderRadius: "16px",
                          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.14)",
                          width: { xs: "310px", sm: "330px" },
                        },
                      }}
                    >
                      <DateRangePicker
                        startDate={selectedDate?.startDate || (selectedDate?.format ? selectedDate : null)}
                        endDate={selectedDate?.endDate || (selectedDate?.format ? selectedDate : null)}
                        onApply={(start, end) => {
                          if (!start && !end) {
                            setSelectedDate(null);
                          } else {
                            const s = start || end;
                            const e = end || start;
                            setSelectedDate({
                              startDate: s.isAfter(e) ? e : s,
                              endDate: s.isAfter(e) ? s : e,
                            });
                          }
                          setDateRangeAnchor(null);
                        }}
                        onClear={() => {
                          setSelectedDate(null);
                          setDateRangeAnchor(null);
                        }}
                        onClose={() => setDateRangeAnchor(null)}
                      />
                    </Popover>
                    {/* Filter Button */}
                    <div
                      onClick={handleOpenFilter}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "4px 8px",
                        borderRadius: "24px",
                        backgroundColor: activeFilterLabel ? "#ffffff" : "#ffffff",
                        boxShadow: "0 0 6px rgba(0,0,0,0.08)",
                        cursor: "pointer",
                        fontFamily: "Work Sans",
                        fontSize: "14px",
                        color: "#14285A",
                        minHeight: "40px",
                        marginLeft: "10px",
                        border: activeFilterLabel ? "1px solid #007c92" : "none",
                      }}
                    >
                      <FilterAltIcon
                        style={{
                          color: "#007c92",
                          fontSize: "20px",
                          marginLeft: "4px"
                        }}
                      />
                      
                      {!activeFilterLabel && (
                         <span style={{ color: "#9aa7b2", marginRight: "8px" }}>Filter</span>
                      )}

                      {statusFilter && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#e8f4f7",
                            padding: "2px 10px",
                            borderRadius: "15px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#007c92",
                            border: "1px solid #007c92"
                          }}
                        >
                          {STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatusFilter(null);
                            }}
                            sx={{ ml: 0.5, p: 0, color: "#007c92" }}
                          >
                            <ClearIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}

                      {operatorStatusFilter && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#fdf3eb",
                            padding: "2px 10px",
                            borderRadius: "15px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#c26510",
                            border: "1px solid #c26510",
                          }}
                        >
                          {OPERATOR_STATUS_OPTIONS.find((o) => o.value === operatorStatusFilter)?.chipLabel}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (setOperatorStatusFilter) setOperatorStatusFilter(null);
                            }}
                            sx={{ ml: 0.5, p: 0, color: "#c26510" }}
                          >
                            <ClearIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}

                      {deviceFilter && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#e8f4f7",
                            padding: "2px 10px",
                            borderRadius: "15px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#007c92",
                            border: "1px solid #007c92",
                            textTransform: "capitalize"
                          }}
                        >
                          {deviceFilter}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeviceFilter(null);
                            }}
                            sx={{ ml: 0.5, p: 0, color: "#007c92" }}
                          >
                            <ClearIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                    </div>
                    <Menu
                      anchorEl={filterAnchor}
                      open={Boolean(filterAnchor)}
                      onClose={handleCloseFilter}
                      PaperProps={{
                        style: { minWidth: "200px" },
                      }}
                    >
                      {/* Status Section */}
                      <MenuItem
                        disabled
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#9aa7b2",
                          fontFamily: "Work Sans",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          opacity: 1,
                          paddingBottom: "2px",
                        }}
                      >
                        By Model Status
                      </MenuItem>
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          onClick={() => handleStatusSelect(opt.value)}
                          selected={statusFilter === opt.value}
                          style={{ 
                            fontFamily: "Work Sans", 
                            paddingLeft: "20px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "10px"
                          }}
                        >
                          <span>{opt.label}</span>
                          {filterMetadata?.status?.[opt.value] !== undefined && (
                            <Box component="span" sx={{ 
                              fontSize: "11px", 
                              backgroundColor: "#f0f7f8", 
                              padding: "2px 8px", 
                              borderRadius: "10px",
                              color: "#316C7A",
                              fontWeight: 600
                            }}>
                              {filterMetadata.status[opt.value]}
                            </Box>
                          )}
                        </MenuItem>
                      ))}

                      {/* Operator Override Section */}
                      <MenuItem
                        disabled
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#9aa7b2",
                          fontFamily: "Work Sans",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          opacity: 1,
                          paddingBottom: "2px",
                          borderTop: "1px solid #eee",
                          marginTop: "4px",
                        }}
                      >
                        By Operator Override
                      </MenuItem>
                      {OPERATOR_STATUS_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          onClick={() => handleOperatorStatusSelect(opt.value)}
                          selected={operatorStatusFilter === opt.value}
                          style={{
                            fontFamily: "Work Sans",
                            paddingLeft: "20px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span>{opt.label}</span>
                          {filterMetadata?.operator_status?.[opt.value] !== undefined && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "11px",
                                backgroundColor: "#f0f7f8",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                color: "#316C7A",
                                fontWeight: 600,
                              }}
                            >
                              {filterMetadata.operator_status[opt.value]}
                            </Box>
                          )}
                        </MenuItem>
                      ))}

                      {/* Device Section */}
                      {deviceOptions.length > 0 && [
                        <MenuItem
                          key="__device_header__"
                          disabled
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            color: "#9aa7b2",
                            fontFamily: "Work Sans",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            opacity: 1,
                            paddingBottom: "2px",
                            borderTop: "1px solid #eee",
                            marginTop: "4px",
                          }}
                        >
                          By Device
                        </MenuItem>,
                        ...deviceOptions.map((d) => (
                          <MenuItem
                            key={d}
                            onClick={() => handleDeviceSelect(d)}
                            selected={deviceFilter === d}
                            style={{ 
                              textTransform: "capitalize", 
                              fontFamily: "Work Sans", 
                              paddingLeft: "20px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "10px"
                            }}
                          >
                            <span>{d}</span>
                            {filterMetadata?.devices?.[d] !== undefined && (
                              <Box component="span" sx={{ 
                                fontSize: "11px", 
                                backgroundColor: "#f0f7f8", 
                                padding: "2px 8px", 
                                borderRadius: "10px",
                                color: "#316C7A",
                                fontWeight: 600
                              }}>
                                {filterMetadata.devices[d]}
                              </Box>
                            )}
                          </MenuItem>
                        )),
                      ]}
                    </Menu>

                    {/* Export Options */}
                    <Box
                      onClick={handleOpenExport}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "4px 8px",
                        borderRadius: "24px",
                        backgroundColor: "#f6fbfc",
                        border: "1px solid #c9d6dc",
                        cursor: exportLoading ? "default" : "pointer",
                        fontFamily: "Work Sans",
                        fontSize: "14px",
                        color: "#14285A",
                        minHeight: "40px",
                        opacity: exportLoading ? 0.6 : 1,
                        marginLeft: "10px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#e8f4f7",
                          borderColor: "#007c92",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          transform: "translateY(-1px)"
                        }
                      }}
                    >
                      {exportLoading ? (
                        <CircularProgress size={18} sx={{ color: "#007c92", ml: 1 }} />
                      ) : (
                        <DownloadIcon
                          style={{
                            color: "#007c92",
                            fontSize: "20px",
                            marginLeft: "4px"
                          }}
                        />
                      )}
                      {!isCompact && (
                        <span style={{ color: "#007c92", fontWeight: 600, marginRight: "8px" }}>
                          {exportLoading ? "Exporting..." : "Export"}
                        </span>
                      )}
                    </Box>
                    <Menu
                      anchorEl={exportAnchor}
                      open={Boolean(exportAnchor)}
                      onClose={handleCloseExport}
                      PaperProps={{ style: { minWidth: "180px" } }}
                    >
                      <MenuItem
                        disabled
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#9aa7b2",
                          fontFamily: "Work Sans",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          opacity: 1,
                          paddingBottom: "2px",
                        }}
                      >
                        Export As
                      </MenuItem>
                      <MenuItem onClick={() => triggerExport('excel')} style={{ fontFamily: "Work Sans" }}>
                        <DescriptionIcon sx={{ fontSize: 20, mr: 1.5, color: "#1D6F42" }} />
                        Excel (Spreadsheet)
                      </MenuItem>
                      <MenuItem onClick={() => triggerExport('pdf')} style={{ fontFamily: "Work Sans" }}>
                        <PictureAsPdfIcon sx={{ fontSize: 20, mr: 1.5, color: "#D32F2F" }} />
                        PDF (Report)
                      </MenuItem>
                    </Menu>

                    {/* Export Confirmation Dialog */}
                    <Dialog 
                      open={confirmDialogOpen} 
                      onClose={() => setConfirmDialogOpen(false)}
                      PaperProps={{
                        sx: { borderRadius: "16px", padding: "8px", minWidth: "400px" }
                      }}
                    >
                      <DialogTitle sx={{ fontFamily: "Work Sans", fontWeight: 700, color: "#14285A", display: "flex", alignItems: "center", gap: 1 }}>
                        <DownloadIcon sx={{ color: "#007c92" }} />
                        Confirm Export
                      </DialogTitle>
                      <DialogContent>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ color: "#9aa7b2", mb: 1, fontFamily: "Work Sans" }}>
                            You are about to export event data with the following filters:
                          </Typography>
                          
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 2 }}>
                            <Chip label={`Format: ${pendingFormat?.toUpperCase()}`} size="small" sx={{ backgroundColor: "#f0f7f8", color: "#007c92", fontWeight: 600 }} />
                            {searchTerm && <Chip label={`Search: ${searchTerm}`} size="small" />}
                            {selectedDate && <Chip label={`Date: ${formatDateRangeLabel(selectedDate)}`} size="small" />}
                            {statusFilter && <Chip label={`Status: ${statusFilter}`} size="small" />}
                            {deviceFilter && <Chip label={`Device: ${deviceFilter}`} size="small" />}
                            {!searchTerm && !selectedDate && !statusFilter && !deviceFilter && <Chip label="All Records" size="small" />}
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ fontFamily: "Work Sans", fontWeight: 600 }}>
                              Total events found:
                            </Typography>
                            <Typography sx={{ fontFamily: "Work Sans", fontWeight: 700, color: "#007c92", fontSize: "1.2rem" }}>
                              {loading ? <CircularProgress size={20} sx={{ color: "#007c92" }} /> : (props.totalCount || 0)}
                            </Typography>
                          </Box>

                          {(props.totalCount || 0) > (pendingFormat === 'pdf' ? 100 : 1000) && (
                            <Alert severity="warning" sx={{ mt: 2, borderRadius: "12px" }}>
                              {pendingFormat === 'pdf' ? (
                                `PDF exports are limited to 100 records for immediate download.`
                              ) : (
                                `Excel exports are limited to 1000 records for immediate download.`
                              )}
                              <br/>Use <b>Export Full</b> for the complete dataset. The file will be available in the <b>Settings &gt; Exports</b> tab once ready.
                            </Alert>
                          )}
                        </Box>
                      </DialogContent>
                      <DialogActions sx={{ padding: "16px", gap: 1 }}>
                        <Button 
                          onClick={() => setConfirmDialogOpen(false)}
                          sx={{ color: "#9aa7b2", textTransform: "none", fontFamily: "Work Sans" }}
                        >
                          Cancel
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        {(props.totalCount || 0) > (pendingFormat === 'pdf' ? 100 : 1000) && (
                          <Button 
                            onClick={() => handleExecuteExport('email')} 
                            variant="outlined" 
                            startIcon={<CheckCircleOutlineIcon />}
                            sx={{ 
                              color: "#007c92", 
                              borderColor: "#007c92",
                              "&:hover": { borderColor: "#005f70", backgroundColor: "#f0f7f8" },
                              textTransform: "none",
                              fontFamily: "Work Sans",
                              borderRadius: "12px"
                            }}
                          >
                            Export Full
                          </Button>
                        )}
                        <Button 
                          onClick={() => handleExecuteExport('download')} 
                          variant="contained" 
                          sx={{ 
                            backgroundColor: "#49758d",
                            "&:hover": { backgroundColor: "#3a5e71" },
                            textTransform: "none",
                            fontFamily: "Work Sans",
                            borderRadius: "12px",
                            padding: "8px 24px"
                          }}
                        >
                          Download
                        </Button>
                      </DialogActions>
                    </Dialog>

                    <Snackbar
                      open={openSnackbar}
                      autoHideDuration={6000}
                      onClose={() => setOpenSnackbar(false)}
                      message={snackbarMessage}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
          
          {props.userBtn && (
            <div style={{ display: "flex" }}>
              <Button
                style={{
                  fontSize: "12px",
                  borderRadius: "10px",
                  backgroundColor: "#49758d",
                  color: "#fff",
                  border: "1px solid #49758d",
                  fontFamily: "Work Sans",
                }}
                onClick={props.addUsers}
                variant="contained"
              >
                Add Users
              </Button>
              {/* <Button
                style={
                  props.miniMize
                    ? {
                        marginLeft: "12px",
                        width: "150px",
                        fontSize: "12px",
                        borderRadius: "10px",
                        backgroundColor: "#49758d",
                        color: "#fff",
                        border: "1px solid #49758d",
                        fontFamily: "Work Sans",
                      }
                    : {
                        marginLeft: "12px",
                        width: "150px",
                        fontSize: "12px",
                        borderRadius: "10px",
                        backgroundColor: "#49758d",
                        color: "#fff",
                        border: "1px solid #49758d",
                        fontFamily: "Work Sans",
                      }
                }
                onClick={props.viewRealTime}
                variant="contained"
              >
                {props.miniMize ? "Continue With Order" : "New Order"}
              </Button> */}
            </div>
          )}
          {props.registerDevice && (
            <Button
              style={{
                width: "150px",
                fontSize: "12px",
                borderRadius: "10px",
                backgroundColor: "#1976d2",
                color: "#fff",
              }}
              onClick={() => navigate("register-device")}
              variant="contained"
            >
              Register New Device
            </Button>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
};

export default AppBarCommon;
